import { BaseSyntheticEvent, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { Icon24 } from 'cio-design-library/icons'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors } from 'builder/modules/resumeEditor'
import { FetchStatuses } from 'builder/modules/constants'
import { ButtonV2 } from 'builder/components/Button'
import { TextAreaField } from 'builder/components/TextField'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { EditorMobileFooter } from 'builder/components/PreviewPanel'
import { useResponsiveButtonSize } from 'builder/hooks/useResponsiveButtonSize'
import { BodyTextCaption, JobPostingContainer, Title } from '../../styles'
import { JobPostingProps } from '../../types'
import { LinkButton } from '../../components/LinkButton/LinkButton'
import {
  BottomContainer,
  ButtonsContainer,
  Container,
  ContentRow,
  MainContainer,
  MobileBottomContainer,
  MobileButton,
  MobileButtonsContainer,
  MobileLinkButtonContainer,
  StyledTextField,
  TopContainer,
} from './styles'

type JobPostingFieldErrors = {
  companyName: boolean
  jobTitle: boolean
  role: boolean
}

const translationKey = 'builder.resume_optimizer.job_posting.no_match'

export const ManualInputStep = (props: JobPostingProps) => {
  const { children, canScrollBottom } = props
  const dispatch = useDispatch()
  const { i18n } = useI18n()
  const resume = useTypedSelector(selectors.resume)
  const { fetchScoreStatus, manualInput } = useTypedSelector(selectors.optimizerSection)
  const [errors, setErrors] = useState<JobPostingFieldErrors>({
    companyName: false,
    jobTitle: false,
    role: false,
  })

  const isLoading = fetchScoreStatus === FetchStatuses.loading
  const emptyErrorText = i18n.t('builder.ai_resume_draft.linkedin.link_empty')
  const { isTablet } = useMediaQueries()
  const buttonSize = useResponsiveButtonSize()

  useEffect(() => {
    trackInternalEvent('see_resume_optimizer_error_screen', { label: 'with_manual_input' })
  }, [])

  const onFieldChange = (event: BaseSyntheticEvent) => {
    const { name, value } = event.target
    dispatch(actions.setResumeOptimizerManualInput(event.target))

    if (value && errors[name as keyof JobPostingFieldErrors]) {
      setErrors({
        ...errors,
        [name]: false,
      })
    }
  }

  const onTryAgain = () => {
    trackInternalEvent('click_try_again_resume_optimizer_error_screen', {
      label: 'with_manual_input',
    })
    dispatch(actions.setShowManualInputScreen(false))
    dispatch(actions.resetResumeOptimizerProcess())
  }

  const onContinue = () => {
    const { companyName, jobTitle, role } = manualInput
    if (!companyName || !jobTitle || !role) {
      setErrors({
        companyName: !companyName,
        jobTitle: !jobTitle,
        role: !role,
      })
    } else {
      trackInternalEvent('click_continue_resume_optimizer_error_screen')
      dispatch(
        actions.checkResumeScore({
          jobPostingManualInput: {
            companyName,
            jobTitle,
            description: role,
          },
          resumeId: resume?.id,
        }),
      )
    }
  }

  const onCancel = () => {
    dispatch(actions.setShowManualInputScreen(false))
  }

  return (
    <>
      <JobPostingContainer>
        <Container>
          <TopContainer>
            <Title>{i18n.t(`${translationKey}.title`)}</Title>
            <BodyTextCaption>{i18n.t(`${translationKey}.content`)}</BodyTextCaption>
          </TopContainer>
          <MainContainer>
            <ContentRow>
              <StyledTextField
                value={manualInput.companyName}
                name="companyName"
                onChange={onFieldChange}
                label={i18n.t(`${translationKey}.company_name`)}
                placeholder={i18n.t(`${translationKey}.company_name`)}
                error={errors.companyName && emptyErrorText}
                autoFocus={true}
              />
              <StyledTextField
                value={manualInput.jobTitle}
                name="jobTitle"
                onChange={onFieldChange}
                label={i18n.t(`${translationKey}.job_title`)}
                placeholder={i18n.t(`${translationKey}.job_title`)}
                error={errors.jobTitle && emptyErrorText}
              />
            </ContentRow>
            <TextAreaField
              value={manualInput.role}
              name="role"
              onChange={onFieldChange}
              rows={6}
              label={i18n.t(`${translationKey}.role_description`)}
              placeholder={i18n.t(`${translationKey}.role_description`)}
              error={errors.role && emptyErrorText}
            />
          </MainContainer>
          {!isTablet && (
            <BottomContainer>
              <LinkButton onClick={onTryAgain} icon={<Icon24.Revert />}>
                {i18n.t(`${translationKey}.try_another_link`)}
              </LinkButton>
              <ButtonsContainer>
                <ButtonV2
                  onClick={onCancel}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  theme="ghost"
                  size={buttonSize}
                >
                  {i18n.t('builder.resume_optimizer.job_posting.cancel')}
                </ButtonV2>
                <ButtonV2
                  onClick={onContinue}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  size={buttonSize}
                >
                  {i18n.t('builder.resume_optimizer.job_posting.continue')}
                </ButtonV2>
              </ButtonsContainer>
            </BottomContainer>
          )}
        </Container>
      </JobPostingContainer>
      {children}
      {isTablet && (
        <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>
          <MobileBottomContainer>
            <MobileLinkButtonContainer>
              <LinkButton onClick={onTryAgain} icon={<Icon24.Revert />}>
                {i18n.t(`${translationKey}.try_another_link`)}
              </LinkButton>
            </MobileLinkButtonContainer>
            <MobileButtonsContainer>
              <MobileButton
                onClick={onCancel}
                isLoading={isLoading}
                isDisabled={isLoading}
                theme="ghost"
                size={buttonSize}
              >
                {i18n.t('builder.resume_optimizer.job_posting.cancel')}
              </MobileButton>
              <MobileButton
                onClick={onContinue}
                isLoading={isLoading}
                isDisabled={isLoading}
                size={buttonSize}
              >
                {i18n.t('builder.resume_optimizer.job_posting.continue')}
              </MobileButton>
            </MobileButtonsContainer>
          </MobileBottomContainer>
        </EditorMobileFooter>
      )}
    </>
  )
}
